import {useMemo, useEffect, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {verifyMagicLink, Authenticate} from 'externalAPIs/insurance-eligibility';
import {SurveyData, STORAGE_ITEM_KEY} from 'screens/InsomniaSurvey/utils/surveyData';

interface MagicLinkVerificationResult {
  email: string | null;
  code: string | null;
  verificationResult: Authenticate | null;
  accessToken: string | null;
  error: string | null;
  isLoading: boolean;
}

/**
 * Extracts email from URL search params, preserving + signs.
 * URLSearchParams.get() converts + to space, so we manually decode the email parameter.
 */
const extractEmailFromUrl = (searchParams: URLSearchParams): string | null => {
  if (typeof window === 'undefined') {
    return searchParams.get('email');
  }

  // Get raw email parameter from URL search string to preserve + signs
  const searchString = window.location.search;
  const emailMatch = searchString.match(/[?&]email=([^&]*)/);
  if (emailMatch) {
    // Replace + with %2B before decoding to preserve + signs in email addresses
    return decodeURIComponent(emailMatch[1].replace(/\+/g, '%2B'));
  }

  return searchParams.get('email');
};

/**
 * Checks if a token is expired based on expiresAt timestamp.
 */
const isTokenExpired = (expiresAt: string | undefined): boolean => {
  if (!expiresAt) {
    return true;
  }

  try {
    const expirationDate = new Date(expiresAt);
    const now = new Date();
    // Add a 5 minute buffer to account for clock skew and ensure we refresh before actual expiration
    const bufferMs = 5 * 60 * 1000;
    return expirationDate.getTime() - bufferMs <= now.getTime();
  } catch (error) {
    console.error('Error parsing expiration date', error);
    return true;
  }
};

/**
 * Hook that extracts email and code from URL params, verifies the magic link,
 * and stores the verification result in survey storage.
 * Only verifies if no token exists or if the existing token is expired.
 */
export const useMagicLinkVerification = (): MagicLinkVerificationResult => {
  const [searchParams] = useSearchParams();
  const [verificationResult, setVerificationResult] = useState<Authenticate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Extract email and code from URL params
  const email = useMemo(() => extractEmailFromUrl(searchParams), [searchParams]);
  const code = useMemo(() => searchParams.get('code'), [searchParams]);

  // Check for existing token and stored email/code in survey data
  const existingTokenData = useMemo(() => {
    const surveyData = SurveyData.read() ?? {};
    const accessToken = surveyData.magic_link_access_token;
    const expiresAt = surveyData.magic_link_expires_at;
    const storedEmail = surveyData.magic_link_email;
    const storedCode = surveyData.magic_link_code;

    if (accessToken && !isTokenExpired(expiresAt)) {
      return {
        accessToken,
        expiresIn: surveyData.magic_link_expires_in,
        tokenType: surveyData.magic_link_token_type,
        expiresAt,
        storedEmail,
        storedCode,
      };
    }

    return {
      storedEmail,
      storedCode,
    };
  }, []);

  useEffect(() => {
    const verify = async () => {
      // Check if email or code from query params differs from stored values
      const emailChanged = email && email !== existingTokenData?.storedEmail;
      const codeChanged = code && code !== existingTokenData?.storedCode;
      const shouldReverify = Boolean(emailChanged) || Boolean(codeChanged);

      // If email or code changed, clear all magic_link_* fields from local storage
      if (shouldReverify && typeof window !== 'undefined') {
        const surveyData = SurveyData.read() ?? {};
        const updatedSurveyData = {...surveyData};
        // Remove all magic_link_* fields
        delete updatedSurveyData.magic_link_access_token;
        delete updatedSurveyData.magic_link_expires_in;
        delete updatedSurveyData.magic_link_token_type;
        delete updatedSurveyData.magic_link_expires_at;
        delete updatedSurveyData.magic_link_email;
        delete updatedSurveyData.magic_link_code;
        window.localStorage.setItem(STORAGE_ITEM_KEY, JSON.stringify(updatedSurveyData));
      }

      // If we have a valid existing token and email/code haven't changed, use it without verifying
      if (existingTokenData?.accessToken && !shouldReverify) {
        setVerificationResult({
          accessToken: existingTokenData.accessToken,
          expiresIn: existingTokenData.expiresIn,
          tokenType: existingTokenData.tokenType,
          expiresAt: existingTokenData.expiresAt,
        });
        setIsLoading(false);
        return;
      }

      // Need to verify if:
      // - No valid token exists
      // - Email or code from query params differs from stored values
      setIsLoading(true);
      setError(null);
      setVerificationResult(null);

      if (!email || !code) {
        setError('Email and code are required');
        setIsLoading(false);
        return;
      }

      try {
        const response = await verifyMagicLink(email, code);

        if (response.error) {
          console.error('Error verifying magic link', response.error);
          setError(response.error);
          setIsLoading(false);
          return;
        }

        // Store verification result and email/code in survey storage
        const surveyData = SurveyData.read() ?? {};
        const updatedSurveyData = {
          ...surveyData,
          magic_link_access_token: response.accessToken,
          magic_link_expires_in: response.expiresIn,
          magic_link_token_type: response.tokenType,
          magic_link_expires_at: response.expiresAt,
          magic_link_email: email,
          magic_link_code: code,
        };

        if (typeof window !== 'undefined') {
          window.localStorage.setItem(STORAGE_ITEM_KEY, JSON.stringify(updatedSurveyData));
        }

        setVerificationResult(response);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        console.error('Error verifying magic link', err);
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    verify();
  }, [email, code, existingTokenData]);

  const accessToken = verificationResult?.accessToken ?? existingTokenData?.accessToken ?? null;

  return {
    email,
    code,
    verificationResult,
    accessToken,
    error,
    isLoading,
  };
};
